import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EMPTY_RETAIL_PREFERENCES,
  getRetailPreferences,
  saveRetailPreferences,
  type RetailPreferences,
} from '../api/retail-preferences';

/**
 * Carga y guarda las preferencias de retail de la exportadora.
 *
 * La UI las edita de a un clic (seguir un SKU, asignar un valor), así que el
 * guardado va con debounce y en cola: mientras hay un request en vuelo el
 * siguiente cambio espera, y al terminar se manda el último estado. Como la
 * configuración es compartida, si otro usuario guardó primero descartamos lo
 * local y mostramos lo que quedó en el servidor.
 */

const SAVE_DEBOUNCE_MS = 600;

export type RetailPreferencesStatus = 'loading' | 'ready' | 'error';
export type RetailSaveState = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

export function useRetailPreferences(exporterId: number, speciesId: number) {
  const [preferences, setPreferences] = useState<RetailPreferences>(
    EMPTY_RETAIL_PREFERENCES
  );
  const [status, setStatus] = useState<RetailPreferencesStatus>('loading');
  const [saveState, setSaveState] = useState<RetailSaveState>('idle');
  const [reloadToken, setReloadToken] = useState(0);

  const versionRef = useRef<number | null>(null);
  const pendingRef = useRef<RetailPreferences | null>(null);
  const savingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sube en cada cambio de scope o unmount para descartar respuestas viejas.
  const scopeRef = useRef(0);

  useEffect(() => {
    const scope = ++scopeRef.current;
    pendingRef.current = null;
    savingRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('loading');
    setSaveState('idle');

    getRetailPreferences(exporterId, speciesId)
      .then(({ version, ...loaded }) => {
        if (scope !== scopeRef.current) return;
        versionRef.current = version;
        setPreferences(loaded);
        setStatus('ready');
      })
      .catch(() => {
        if (scope !== scopeRef.current) return;
        setStatus('error');
      });

    // No hace falta invalidar el scope acá: al cambiar de exportadora o
    // especie este mismo efecto vuelve a correr y lo sube. Si el componente se
    // desmonta con un guardado en vuelo, ese guardado termina igual (es lo que
    // queremos) y su setState queda en no-op.
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [exporterId, speciesId, reloadToken]);

  const flush = useCallback(async () => {
    if (savingRef.current) return;
    const payload = pendingRef.current;
    if (!payload) return;

    const scope = scopeRef.current;
    savingRef.current = true;
    pendingRef.current = null;

    try {
      const result = await saveRetailPreferences(
        exporterId,
        speciesId,
        payload,
        versionRef.current
      );
      if (scope !== scopeRef.current) return;

      if (result.status === 'conflict') {
        pendingRef.current = null;
        const { version, ...fresh } = await getRetailPreferences(
          exporterId,
          speciesId
        );
        if (scope !== scopeRef.current) return;
        versionRef.current = version;
        setPreferences(fresh);
        setSaveState('conflict');
        return;
      }

      versionRef.current = result.version;
      setSaveState(pendingRef.current ? 'saving' : 'saved');
    } catch {
      if (scope === scopeRef.current) setSaveState('error');
    } finally {
      savingRef.current = false;
      if (scope === scopeRef.current && pendingRef.current) void flush();
    }
  }, [exporterId, speciesId]);

  const update = useCallback(
    (next: RetailPreferences) => {
      setPreferences(next);
      pendingRef.current = next;
      setSaveState('saving');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { preferences, status, saveState, update, reload };
}
